/* eslint-disable @next/next/no-img-element */
import {isDev} from 'config';
import Script from 'next/script';
import React, {FC, useEffect, useState} from 'react';

// Delay in milliseconds before loading analytics scripts
const ANALYTICS_DELAY = 5000; // 3 seconds

export const Scripts: FC = React.memo(() => {
  const [shouldLoadAnalytics, setShouldLoadAnalytics] = useState(false);

  useEffect(() => {
    if (isDev) return;

    // Use requestIdleCallback for better performance, fallback to setTimeout
    const scheduleAnalytics = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => setShouldLoadAnalytics(true), {timeout: ANALYTICS_DELAY});
      } else {
        setTimeout(() => setShouldLoadAnalytics(true), ANALYTICS_DELAY);
      }
    };

    // Also load on user interaction for better engagement tracking
    const loadOnInteraction = () => {
      setShouldLoadAnalytics(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', loadOnInteraction);
      window.removeEventListener('mousemove', loadOnInteraction);
      window.removeEventListener('touchstart', loadOnInteraction);
    };

    // Start delay timer
    scheduleAnalytics();

    // Also trigger on first user interaction (whichever comes first)
    window.addEventListener('scroll', loadOnInteraction, {once: true, passive: true});
    window.addEventListener('mousemove', loadOnInteraction, {once: true, passive: true});
    window.addEventListener('touchstart', loadOnInteraction, {once: true, passive: true});

    return cleanup;
  }, []);

  if (isDev) return null;

  return (
    <>
      {/* Load OneSignal immediately as it's user-facing functionality */}
      <Script src="https://cdn.onesignal.com/sdks/OneSignalSDK.js" async strategy="afterInteractive" />

      {/* Delay analytics scripts until after page is interactive and delay has passed */}
      {shouldLoadAnalytics && (
        <>
          {/* Google Analytics (Universal Analytics - consider migrating to GA4) */}
          <Script
            id="google-analytics"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function (i, s, o, g, r, a, m) {
                  i['GoogleAnalyticsObject'] = r;
                  i[r] = i[r] || function () {
                    (i[r].q = i[r].q || []).push(arguments)
                  }, i[r].l = 1 * new Date();
                  a = s.createElement(o),
                      m = s.getElementsByTagName(o)[0];
                  a.async = 1;
                  a.src = g;
                  m.parentNode.insertBefore(a, m)
                })(window, document, 'script', '//www.google-analytics.com/analytics.js', 'ga');
                ga('create', 'UA-56267619-1', 'auto');
                ga('send', 'pageview');
              `,
            }}
          />

          {/* Yandex Metrika */}
          <Script
            id="yandex-metrika"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(m,e,t,r,i,k,a){
                m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
                m[i].l=1*new Date();
                for (var j = 0; j < document.scripts.length; j++) {
                  if (document.scripts[j].src === r) { return; }
                }
                k=e.createElement(t),a=e.getElementsByTagName(t)[0],
                k.async=1,k.src=r,a.parentNode.insertBefore(k,a)
              })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

              ym(100560690, "init", {
                clickmap:true,
                trackLinks:true,
                accurateTrackBounce:true,
                webvisor:true
              });
            `,
            }}
          />
        </>
      )}

      <noscript>
        <div>
          {/* just for yandex metrica */}
          <img src="https://mc.yandex.ru/watch/100560690" style={{position: 'absolute', left: '-9999px'}} alt="" />
        </div>
      </noscript>
    </>
  );
});
